import { AppDispatch } from 'redux/store'
import {
  fetchApplicationId,
  fetchApplicationConfig,
  fetchSourceId,
  fetchSoureOrOwnerConfig,
  postOwnerConfig,
  patchOwnerConfig,
} from 'redux/thunks/TextAnalysis'
import { updateAppConfig } from 'redux/slices/TextAnalysis'

const capitalizeFirst = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1)

const getAppAndSourceIds = (configData: any) => {
  const appId = configData?.scope?.id
  let sourceId = null
  const rules = configData?.configurations?.rules

  if (rules && rules.length > 0) {
    const sentimentCondition = rules[0]?.sentiment_toggle_condition?.and
    if (sentimentCondition) {
      const sourceCondition = sentimentCondition.find(
        (condition: any) => condition?.scope?.type === 'source',
      )
      sourceId = sourceCondition?.scope?.id
    }
  }
  return { appId, sourceId }
}

export const getTAConfigDetails = async (dispatch: AppDispatch) => {
  dispatch(updateAppConfig({ doneAnalysis: false }))

  const configData = sessionStorage.getItem('TA_CONFIG')
  if (configData) {
    getSourceId(dispatch, configData)
    return
  }

  /* 1. FETCH APPLICATION ID */
  const appIdOutput: any = await dispatch(fetchApplicationId())
  const appId = appIdOutput?.payload?.data?.[0]?.id ?? null
  if (!appId) return

  getAppConfig(dispatch, appId)
}

/* 2. FETCH APPLICATION CONFIG */
const getAppConfig = async (dispatch: AppDispatch, appId: string) => {
  const appConfigOutput: any = await dispatch(fetchApplicationConfig({ appId }))

  const { scope, configurations } =
    appConfigOutput?.payload?.data?.[0]?.attributes || {}

  if (!scope || !configurations) return

  const configData = JSON.stringify({ scope, configurations })
  sessionStorage.setItem('TA_CONFIG', configData)

  getSourceId(dispatch, configData)
}

/* 3. FETCH SOURCE ID */
const getSourceId = async (dispatch: AppDispatch, configData: string) => {
  const parsedConfig = JSON.parse(configData)

  if (!configData.includes('<source_id>')) {
    getSourceConfig(dispatch, parsedConfig)
    return
  }

  const appId = parsedConfig?.scope?.id
  const sourceIdOutput: any = await dispatch(fetchSourceId({ appId }))
  const sourceId = sourceIdOutput?.payload?.data?.[0]?.id ?? null

  // Disable the Analysis buttons
  if (!sourceId) {
    dispatch(
      updateAppConfig({
        platformSentiment: false,
        platformThemes_keyphrases: false,
        doneAnalysis: true,
      }),
    )
    return
  }

  // Replace <source_id> with the actual sourceId
  const updatedConfigData = configData.replace(
    /"<source_id>"/g,
    `"${sourceId}"`,
  )
  sessionStorage.setItem('TA_CONFIG', updatedConfigData)
  getSourceConfig(dispatch, JSON.parse(updatedConfigData))
}

/* 4. FETCH SOURCE CONFIG (To get the Platform Toggle Status) */
const getSourceConfig = async (dispatch: AppDispatch, configData: any) => {
  const { appId, sourceId } = getAppAndSourceIds(configData)

  const response: any = await dispatch(
    fetchSoureOrOwnerConfig({ appId, sourceId, source: 'source' }),
  )
  const data = response?.payload?.data
  if (!data) return

  const features = data[0]?.attributes?.configurations?.features ?? []

  const platformToggles = features.reduce(
    (acc: Record<string, boolean>, { type, status }: any) => {
      acc[`platform${capitalizeFirst(type)}`] = status
      return acc
    },
    {},
  )

  dispatch(updateAppConfig(platformToggles))
  getOwnerConfig(dispatch)
}

/* 5. FETCH OWNER CONFIG */
export const getOwnerConfig = async (dispatch: AppDispatch) => {
  const configData = sessionStorage.getItem('TA_CONFIG')
  if (!configData) return

  const { appId, sourceId } = getAppAndSourceIds(JSON.parse(configData))

  const response: any = await dispatch(
    fetchSoureOrOwnerConfig({ appId, sourceId, source: 'owner' }),
  )

  const data = response?.payload?.data?.[0]
  const features = data?.attributes?.configurations?.features ?? []
  const ownerConfigId = data?.id ?? null

  if (!ownerConfigId) {
    dispatch(
      updateAppConfig({
        sentimentToggle: false,
        doneAnalysis: true,
      }),
    )
    createOwnerConfig(dispatch, appId, sourceId)
    return
  }

  const toggleStates: Record<string, boolean> = {}

  features.forEach((feature: { type: string; status: boolean }) => {
    toggleStates[`${feature.type}Toggle`] = feature.status ?? false
  })

  dispatch(
    updateAppConfig({
      ...toggleStates,
      ownerConfigId,
      doneAnalysis: true,
    }),
  )
}

/* 6. CREATE OWNER CONFIG */
const createOwnerConfig = async (
  dispatch: AppDispatch,
  appId: string,
  sourceId: string,
) => {
  const response: any = await dispatch(postOwnerConfig({ appId, sourceId }))
  const configId = response?.payload?.data?.id ?? null

  if (!configId) return
  dispatch(updateAppConfig({ ownerConfigId: configId }))
}

/* 7. UPDATE OWNER Toggles (Site Toggles)*/
export const updateOwnerToggles = async (
  dispatch: AppDispatch,
  toggles: Record<string, boolean | undefined>,
  configId: string,
) => {
  const configData = sessionStorage.getItem('TA_CONFIG')
  if (!configData) return

  const { appId, sourceId } = getAppAndSourceIds(JSON.parse(configData))

  const features: { type: string; status: boolean }[] = []

  for (const [type, status] of Object.entries(toggles)) {
    if (typeof status === 'boolean') {
      features.push({ type, status })
    }
  }

  if (features.length === 0) return

  // 1. Patch API
  await dispatch(
    patchOwnerConfig({
      appId,
      sourceId,
      configId,
      features,
    }),
  )

  // 2. Update Redux state for each toggle
  const updatedToggles: Record<string, boolean> = {}

  features.forEach(({ type, status }) => {
    updatedToggles[`${type}Toggle`] = status
  })

  dispatch(updateAppConfig(updatedToggles))
}
